import { useState, useEffect } from 'react';
import { getLiveDataUrl } from '../live-data';
import { useProperty } from './context';

const LIVE_COLLECTIONS: ReadonlySet<string> = new Set([
  'floorplans',
  'special_offers',
]);

const liveCache = new Map<string, { data: unknown[]; timestamp: number }>();
const LIVE_CACHE_TTL = 60_000;
const inflightRequests = new Map<string, Promise<unknown[] | null>>();

/**
 * Fetch a live collection from the edge worker.
 * Returns the collection array, or null if live data is unavailable.
 *
 * Only fetches collections listed in LIVE_COLLECTIONS (floorplans,
 * special_offers). For any other collection the hook is a no-op that
 * returns null — safe to call unconditionally (React rules of hooks).
 */
export function useLiveCollection<T>(collection: string): T[] | null {
  const property = useProperty();
  const propertyId = property.multihub_property_id;
  const workerUrl = getLiveDataUrl();
  const isLive = LIVE_COLLECTIONS.has(collection);

  const [data, setData] = useState<T[] | null>(() => {
    if (!isLive || !workerUrl || !propertyId) return null;
    const cached = liveCache.get(`${propertyId}:${collection}`);
    if (cached && Date.now() - cached.timestamp < LIVE_CACHE_TTL) {
      return cached.data as T[];
    }
    return null;
  });

  useEffect(() => {
    if (!isLive || !workerUrl || !propertyId) return;

    const cacheKey = `${propertyId}:${collection}`;
    const cached = liveCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < LIVE_CACHE_TTL) {
      setData(cached.data as T[]);
      return;
    }

    let request = inflightRequests.get(cacheKey);
    if (!request) {
      request = fetch(`${workerUrl}/collections/${propertyId}/${collection}`)
        .then(r => r.ok ? r.json() : null)
        .then(result => {
          if (Array.isArray(result)) {
            liveCache.set(cacheKey, { data: result, timestamp: Date.now() });
          }
          return result;
        })
        .catch(() => null)
        .finally(() => inflightRequests.delete(cacheKey));
      inflightRequests.set(cacheKey, request);
    }

    let cancelled = false;
    request.then(result => {
      if (!cancelled && Array.isArray(result)) {
        setData(result as T[]);
      }
    });

    return () => { cancelled = true; };
  }, [isLive, workerUrl, propertyId, collection]);

  return isLive ? data : null;
}
